import { useEffect, useRef } from 'react';
import { animate, createScope, stagger, onScroll, svg } from 'animejs';

/**
 * useAnimeSvg — anime.js v4 SVG animation utilities.
 *
 * 1. createDrawable with stagger: Line-drawing animation for route paths
 *    connecting continents on the Our Locations map.
 * 2. createDrawable (generic): Line-draw any [data-anime-draw] SVG paths.
 * 3. feTurbulence/feDisplacementMap: Liquid distortion on headings.
 * 4. svg.morphTo: Morph between SVG shapes.
 */
function useAnimeSvg({
  routeSelector = '.drx-map-route',
  drawableSelector = '[data-anime-draw]',
  turbulenceSelector = '[data-anime-turbulence]',
  morphSelector = '[data-anime-morph]',
  rootRef: externalRootRef,
} = {}) {
  const internalRootRef = useRef(null);
  const rootRef = externalRootRef || internalRootRef;
  const scopeRef = useRef(null);

  useEffect(() => {
    if (!rootRef.current) return undefined;

    scopeRef.current = createScope({ root: rootRef.current }).add(() => {
      const root = rootRef.current;

      // 1. Route paths: drawable line-draw with stagger for continent connections
      const routeEls = root.querySelectorAll(routeSelector);
      if (routeEls.length) {
        try {
          const drawables = svg.createDrawable(routeEls);
          let scrollOpt = true;
          const mapStage = root.querySelector('.drx-map-stage');
          try {
            scrollOpt = onScroll({
              target: mapStage || routeEls[0],
              enter: 'bottom center',
              sync: true,
            });
          } catch (e) {
            // Fallback to autoplay
          }
          animate(drawables, {
            draw: ['0 0', '0 1'],
            ease: 'inOut(3)',
            duration: 2400,
            delay: stagger(300),
            loop: true,
            alternate: true,
            autoplay: scrollOpt,
          });
        } catch (e) {
          // Graceful fallback
        }
      }

      // 2. Generic drawable: line-draw any [data-anime-draw] elements (non-route)
      const drawEls = root.querySelectorAll(`${drawableSelector}:not(${routeSelector})`);
      if (drawEls.length) {
        drawEls.forEach((el) => {
          try {
            const [drawable] = svg.createDrawable(el);
            let scrollOpt = true;
            try {
              scrollOpt = onScroll({
                target: el,
                enter: 'bottom center',
                sync: true,
              });
            } catch (e) {
              // Fallback
            }
            animate(drawable, {
              draw: ['0 0', '0 1', '1 1'],
              duration: 2000,
              ease: 'out(3)',
              autoplay: scrollOpt,
            });
          } catch (e) {
            // Skip if not a valid SVG path
          }
        });
      }

      // 3. feTurbulence/feDisplacementMap: liquid distortion on headings
      const turbEls = root.querySelectorAll(turbulenceSelector);
      if (turbEls.length) {
        try {
          animate(Array.from(turbEls), {
            baseFrequency: [0.008, 0.015],
            scale: [4, 8],
            alternate: true,
            loop: true,
            duration: 6000,
            ease: 'inOut(2)',
          });
        } catch (e) {
          // Graceful fallback
        }
      }

      // 4. svg.morphTo: morph between SVG shapes
      const morphEls = root.querySelectorAll(morphSelector);
      if (morphEls.length) {
        morphEls.forEach((el) => {
          const targetShape = el.dataset.animeMorph;
          if (!targetShape) return;
          try {
            animate(el, {
              ...svg.morphTo(targetShape),
              duration: 3000,
              ease: 'inOut(3)',
              alternate: true,
              loop: true,
            });
          } catch (e) {
            // Skip if morph target invalid
          }
        });
      }
    });

    return () => scopeRef.current?.revert();
  }, [routeSelector, drawableSelector, turbulenceSelector, morphSelector, rootRef]);

  return rootRef;
}

export default useAnimeSvg;
